// src/ui/EmojiPanel.jsx — picking the emoji a status starts with.
//
// 🟥 IT USED TO BE A STRIP OF THIRTY BESIDE THE TEXT FIELD, which is both too
//    few to find what you meant and, wrapped over four rows, in the way of the
//    thing it decorates. This is its own container: a grid with room in it and a
//    bar of groups along the bottom, the way every emoji keyboard is arranged.
//
// 🟢 THE BOTTOM BAR FILTERS RATHER THAN SCROLLS. Scroll-spy tabs — where the bar
//    highlights whatever section is under the scroll position — need the grid,
//    the bar and the scroll container to agree at all times, and they disagree
//    the moment the panel is resized or a group is empty. Tapping a group and
//    seeing only that group is what a person predicted would happen, and it is
//    one piece of state instead of three.
//
// 🟥 AND THERE IS NO SEARCH BOX, DELIBERATELY. Searching emoji means a keyword
//    table — "grinning, happy, smile" per character — and without one the only
//    thing a box could match is the eight group names, which is a search that
//    finds nothing anybody typed. A fake search is worse than none: it is a
//    promise the panel cannot keep.
import React, { useState } from 'react';
import { EMOJI_GROUPS } from '../services/emoji';

export default function EmojiPanel({ value, onPick, onClose }) {
  /* 🟢 OPENS WHERE THE CURRENT CHOICE LIVES. Landing on Smileys when the status
     already starts with a flag means hunting for something already chosen. */
  const start = EMOJI_GROUPS.find((g) => g.emojis.includes(String(value || '')));
  const [tab, setTab] = useState(start ? start.id : EMOJI_GROUPS[0].id);
  const group = EMOJI_GROUPS.find((g) => g.id === tab) || EMOJI_GROUPS[0];

  return (
    <div className="em-panel">
      <div className="em-head">
        <b>{group.name}</b>
        <span className="em-count">{group.emojis.length}</span>
        <button className="em-x" onClick={onClose} aria-label="Close">✕</button>
      </div>

      <div className="em-grid" role="listbox" aria-label={group.name}>
        {group.emojis.map((e, i) => (
          <button
            /* 🟥 THE INDEX IS PART OF THE KEY. A few characters appear in two
               groups — ✨ is a smiley and weather, ✅ is a symbol twice — and a
               bare emoji key would collide and drop one of them silently. */
            key={`${e}-${i}`}
            type="button"
            /* 🟥 `role="option"`, because the grid is a listbox. `aria-selected`
               means nothing on a plain button and a screen reader ignores it —
               so the one thing a blind user needs to know here, which emoji is
               currently chosen, would not have been announced. */
            role="option"
            className={`em-cell ${e === value ? 'on' : ''}`}
            title={e}
            aria-selected={e === value}
            onClick={() => onPick(e)}
          >
            {e}
          </button>
        ))}
      </div>

      {/* the bar of groups, labelled with one of their own members — a label
          made of the thing it labels needs no translating */}
      <div className="em-bar" role="tablist">
        {EMOJI_GROUPS.map((g) => (
          <button
            key={g.id}
            type="button"
            role="tab"
            aria-selected={g.id === tab}
            className={`em-tab ${g.id === tab ? 'on' : ''}`}
            title={g.name}
            aria-label={g.name}
            onClick={() => setTab(g.id)}
          >
            {g.icon}
          </button>
        ))}
      </div>
    </div>
  );
}
